/**
 * Shipments UI Plugin Routes
 *
 * Defines all routes for the shipments UI plugin.
 */

import { defineRoutes } from '@lognai/plugin-sdk';

/**
 * Plugin identifier
 */
export const pluginId = 'shipments';

/**
 * All routes for the shipments UI plugin
 */
export const routes = defineRoutes(pluginId, '/shipments', [
  // Landing index for /shipments (hidden from nav; the plugin group header
  // "Shipments" is provided by the app's PLUGIN_METADATA). Nav items below are
  // siblings under that group — no self-nesting — matching the orders plugin.
  {
    path: '/',
    component: () =>
      import('./pages/ShipmentListPage').then((m) => ({
        default: m.ShipmentListPage,
      })),
    name: 'Shipments',
    description: 'Manage shipments and freight',
    permissions: ['shipment:shipment:read'],
    nav: {
      icon: { name: 'Ship', size: 20 },
      showInNav: false,
      order: 40,
    },
  },

  // All Shipments sub-item — appears directly under the "Shipments" nav group
  {
    path: '/all',
    component: () =>
      import('./pages/ShipmentListPage').then((m) => ({
        default: m.ShipmentListPage,
      })),
    name: 'All Shipments',
    description: 'View all shipments',
    permissions: ['shipment:shipment:read'],
    nav: {
      icon: { name: 'Ship', size: 20 },
      showInNav: true,
      order: 1,
    },
  },

  // Global Shipment Radar (live FCL/LCL world map)
  {
    path: '/radar',
    component: () =>
      import('./pages/GlobalShipmentRadarPage').then((m) => ({
        default: m.GlobalShipmentRadarPage,
      })),
    name: 'Shipment Radar',
    description: 'Live FCL/LCL shipment radar',
    permissions: ['shipment:shipment:read'],
    nav: {
      icon: { name: 'Radar', size: 20 },
      showInNav: true,
      order: 0,
    },
  },

  // Milestone settings
  {
    path: '/settings/milestones',
    component: () =>
      import('./pages/MilestoneSettingsPage').then((m) => ({
        default: m.MilestoneSettingsPage,
      })),
    name: 'Milestone Settings',
    description: 'Configure milestone templates',
    permissions: ['shipment:milestone:manage'],
    nav: {
      showInNav: true,
      order: 2,
    },
  },

  // Charge catalog settings
  {
    path: '/settings/charges',
    component: () =>
      import('./pages/ChargeCatalogSettingsPage').then((m) => ({
        default: m.ChargeCatalogSettingsPage,
      })),
    name: 'Charge Catalog',
    description: 'Standardized logistics charges',
    permissions: ['shipment:charge_catalog:manage'],
    nav: {
      showInNav: true,
      order: 3,
    },
  },

  // Create shipment
  {
    path: '/new',
    component: () =>
      import('./pages/ShipmentCreatePage').then((m) => ({
        default: m.ShipmentCreatePage,
      })),
    name: 'Create Shipment',
    description: 'Create a new shipment',
    permissions: ['shipment:shipment:create'],
    parentPath: '/',
    nav: {
      showInNav: false,
    },
  },

  // Edit shipment (registered before /:id so it isn't shadowed)
  {
    path: '/:id/edit',
    component: () =>
      import('./pages/ShipmentEditPage').then((m) => ({
        default: m.ShipmentEditPage,
      })),
    name: 'Edit Shipment',
    description: 'Edit shipment details',
    permissions: ['shipment:shipment:update'],
    parentPath: '/',
    nav: {
      showInNav: false,
    },
  },

  // Shipment detail
  {
    path: '/:id',
    component: () =>
      import('./pages/ShipmentDetailPage').then((m) => ({
        default: m.ShipmentDetailPage,
      })),
    name: 'Shipment Details',
    description: 'View and manage shipment details',
    permissions: ['shipment:shipment:read'],
    parentPath: '/',
    nav: {
      showInNav: false,
    },
  },
]);
